/**
 * WebAuthn feature configuration — the single plug-in seam.
 *
 * When the backend is ready:
 *   1. Flip WEBAUTHN_ENABLED to true (or drive it from an env/remote flag).
 *   2. Set WEBAUTHN_MOCK to false.
 *   3. Verify the ENDPOINTS paths below match the deployed routes.
 * Nothing else in the frontend needs to change.
 *
 * While WEBAUTHN_ENABLED is false the login flow is byte-for-byte the legacy
 * flow: LoginUser stores the token exactly as before and never touches any of
 * the code in this feature module.
 */

// Master flag. false => legacy password-only login, feature fully dormant.
export const WEBAUTHN_ENABLED = true;

// When true, the API layer resolves against an in-memory mock instead of the
// network. MUST be false against the real backend.
export const WEBAUTHN_MOCK = false;

// When true, the on-device WebAuthn ceremony is stubbed (no Touch ID /
// keychain). Keep false to run the real browser ceremony. Must be served from
// a secure context (http://localhost or https).
export const WEBAUTHN_MOCK_CEREMONY = false;

// Analyst IDs that bypass the WebAuthn second factor entirely (testing / break
// glass). A bypassed login only reaches the dashboard if the login response
// still carries a token — the backend must not enforce WebAuthn for these IDs.
export const WEBAUTHN_BYPASS_ANALYST_IDS = [];

// Poll interval (ms) for the pending-approval screen.
export const REGISTRATION_POLL_MS = 20000;

// Label constraints (mirror backend validation: label 1–64 chars).
export const LABEL_MAX_LEN = 64;

// Device types (mirror backend WebauthnDeviceType enum).
export const DEVICE_TYPES = [
  { value: "laptop", label: "Laptop" },
  { value: "desktop", label: "Desktop" },
  { value: "mobile", label: "Mobile" },
  { value: "tablet", label: "Tablet" },
  { value: "other", label: "Other" },
];

/**
 * Endpoint paths, relative to MAIN_API (apiClient baseURL already includes it).
 * MAIN_API already ends in ".../api/", so these are written without a leading
 * "api/" and without a leading slash. Paths match the deployed backend.
 */
export const ENDPOINTS = {
  // Existing login endpoint (extended server-side to return next_step).
  login: "loginAnalyst/v2/",
  logout: "logoutAnalyst/",

  // Authentication (assertion) ceremony.
  authOptions: "webauthn/analyst/authenticate/options/",
  authVerify: "webauthn/analyst/authenticate/verify/",

  // Enrollment (attestation) — request -> approve -> complete.
  registerRequest: "webauthn/analyst/register/request/",
  registerStatus: "webauthn/analyst/register/status/",
  registerOptions: "webauthn/analyst/register/options/",
  registerVerify: "webauthn/analyst/register/verify/",

  // Self-service credential management (ANALYST jwt).
  credentials: "webauthn/analyst/credentials/",
  credentialRename: (id) => `webauthn/analyst/credentials/${id}/`, // PATCH
  credentialDelete: (id) => `webauthn/analyst/credentials/${id}/delete/`, // DELETE
  loginHistory: "webauthn/analyst/login-history/",

  // Admin surface (SENSE_ADMIN jwt).
  adminRequests: "webauthn/admin/registration-requests/",
  adminApprove: (id) => `webauthn/admin/registration-requests/${id}/approve/`,
  adminReject: (id) => `webauthn/admin/registration-requests/${id}/reject/`,
  adminAnalystCredentials: (analystId) => `webauthn/admin/analysts/${analystId}/credentials/`,
  adminDisableCredential: (id) => `webauthn/admin/credentials/${id}/disable/`,
  adminDeleteCredential: (id) => `webauthn/admin/credentials/${id}/delete/`,
  adminSetExpiry: (id) => `webauthn/admin/credentials/${id}/set-expiry/`,
  adminAnalystLoginHistory: (analystId) => `webauthn/admin/analysts/${analystId}/login-history/`,
};

/**
 * next_step values. The login endpoint returns only WEBAUTHN_AUTH or
 * REGISTRATION_REQUIRED (or AUTHENTICATED when the flag is off). PENDING and
 * READY are client-side transitions the flow drives off register/status.
 */
export const NEXT_STEP = {
  WEBAUTHN_AUTH: "webauthn_auth",
  REGISTRATION_REQUIRED: "registration_required",
  REGISTRATION_PENDING: "registration_pending",
  REGISTRATION_READY: "registration_ready",
  AUTHENTICATED: "authenticated", // legacy / flag-off
};
